import type { Game } from '../../game/Game';
import { itemDef } from '../../game/items';
import { bump, openPanel } from '../../ui/app/store';
import { CREW, CROP_BY_ID, MISSION_BY_ID, RESEARCH_BY_ID } from './data';
import { type HavenEvent, gardenView, havenBonus, roman, tickHaven } from './logic';

/** Game-side glue for Haven Isle: the timer system, rewards, stations and research effects. */

export const havenGame: { current: Game | null } = { current: null };

export type HavenTab = 'research' | 'workshop' | 'dispatch' | 'garden';
export const HAVEN_TABS: HavenTab[] = ['research', 'workshop', 'dispatch', 'garden'];

/** Maps a station's name (`data.station`) to the panel tab it opens. */
export function stationTab(name: string): HavenTab {
  const n = name.toLowerCase();
  if (/work|craft|forge|bench|anvil|kiln/.test(n)) return 'workshop';
  if (/dispatch|dock|harbo|pier|ship|crew|board/.test(n)) return 'dispatch';
  if (/garden|farm|field|green|plot|bed/.test(n)) return 'garden';
  return 'research';
}

/** G on a `station` interactable on Haven Isle opens the matching tab. */
export function openStation(game: Game, id: string): boolean {
  const z = game.zone;
  if (!z || (z.layout.kind !== 'haven' && z.layout.id !== 'haven')) return false;
  const it = z.interactables.find((i) => i.id === id);
  if (!it) return false;
  openPanel('haven', stationTab(String(it.data?.station ?? it.data?.tab ?? it.id)));
  return true;
}

function grant(game: Game, ev: HavenEvent): void {
  const prog = game.progression;
  if (!prog) return;
  const hud = game.hud;
  switch (ev.type) {
    case 'research': {
      const def = RESEARCH_BY_ID[ev.id];
      hud.announce(`Research complete — ${def?.name ?? ev.id} ${roman(ev.level)}`, 'success');
      for (const n of ev.recruits) hud.toast(`New crew member: ${n} (${CREW[n]?.trait ?? 'Deckhand'})`, '#7ad8ff');
      break;
    }
    case 'craft': {
      // giveItem mails whatever doesn't fit in the bag.
      prog.giveItem(ev.item, ev.count);
      if (ev.great) hud.toast(`Great Success! Extra ${itemDef(ev.item).name}`, '#ffc207');
      break;
    }
    case 'dispatch': {
      const m = MISSION_BY_ID[ev.mission];
      const label = ev.outcome === 'great' ? 'Great Success' : ev.outcome === 'success' ? 'Success' : 'Failed';
      hud.toast(`Dispatch returned: ${m?.name ?? ev.mission} — ${label}`, ev.outcome === 'fail' ? '#e5a54d' : '#7ad8ff');
      prog.grantDrop({ items: ev.reward.items, equip: [], silver: ev.reward.silver, gold: ev.reward.gold, cards: ev.reward.cards }, `Dispatch: ${m?.name ?? ev.mission}`);
      break;
    }
    case 'level':
      hud.announce(`Haven Isle reached level ${ev.level}`, 'success');
      break;
  }
}

let acc = 0;
const gardenNotified = new Set<number>();

/** Completes Haven timers once per second (also catches up on everything that finished offline). */
export function havenSystem(game: Game, dt: number): void {
  acc += dt;
  if (acc < 1) return;
  acc = 0;
  if (!game.progression) return;
  const now = Date.now();
  const events = tickHaven(game.account, now);
  for (const ev of events) grant(game, ev);
  const g = game.account.haven.garden;
  const v = gardenView(game.account.haven, now);
  if (g && v && v.stage >= 3 && !gardenNotified.has(g.plantedAt)) {
    gardenNotified.add(g.plantedAt);
    game.hud.toast(`Haven garden: ${CROP_BY_ID[g.crop]?.name ?? 'Crop'} ready to harvest`, '#7bd66b');
  }
  if (events.length) {
    bump();
    game.store.saveSoon(game.account);
  }
}

/**
 * Quartermaster research: +N uses of every battle item per instance. Runs as a
 * zone setup, i.e. right after the game resets the per-instance limits.
 */
export function applyQuartermaster(game: Game): void {
  const prog = game.progression;
  const bonus = Math.round(havenBonus(game.account, 'battleItemLimit'));
  if (!prog || bonus <= 0) return;
  for (const [id, n] of prog.instanceUses) prog.instanceUses.set(id, n + bonus);
}
